import toString from 'lodash-es/toString'
import isNumber from 'lodash-es/isNumber'
import filter from 'lodash-es/filter'
import findIndex from 'lodash-es/findIndex'
import sumBy from 'lodash-es/sumBy'
import cloneDeep from 'lodash-es/cloneDeep'
import { TransactionBuilder, Transaction as RawTransaction, ECPairInterface as ECPair, Network, payments, script, address as addressHelper } from 'bitcoinjs-lib'
import selectByBlackjack from 'coinselect'
import selectBySplit from 'coinselect/split'

import { ILogger, IBTCTransactionAddress, IUTXOTransaction, IBTCUnspent, IBTCInput, IBTCOutput, BTCAddress, OneChainError } from '@one-chain/core'
import { IApiProvider, IFastApiProvider, ISlowApiProvider } from './api-providers'
import { isValidAddress, parseRawTransaction } from './helper'

/**
 * Transaction
 *
 * 为什么要在交易对象之上再包一层交易对象？
 *
 * 首先明确三个特点：
 *
 * 1. 对于 BTC 具体交易的构造还不完全理解，不过能够明确的是，交易构造完成后不能修改 output 的 value 。
 * 2. BTC 交易的 fee 是根据交易总体积有多少字节来计算的，但是复杂的情况下比较难在交易构造完成前就算出整个交易的体积，而
 * fee 又是通过定义找零 output 的 value 来实现的，所以构造交易和设置交易的 fee 变成了一个先有鸡还有是先有蛋的问题。
 * 3. 交易 output 中 value 占用的体积是固定的 8 bytes ，所以即使修改 output 的 value ，构造的交易体积不会发生变化。
 * 4. 每个 input 和 output 的体积实际是根据 script 来变化的，并非网上常说的固定体积。
 *
 * 在此特点的基础上，需要实现可多次计算调整交易费率的功能，那么最简单的办法就是：先构造交易，计算 fee，然后重新根据实际
 * fee 再次构造交易。
 *
 * 所以实现了这个类来解决这其中的参数过多、需要保存状态等问题。
 *
 * @export
 * @class Transaction
 * @implements {IUTXOTransaction}
 */
export class Transaction implements IUTXOTransaction {
  protected _apiProvider: IApiProvider
  protected _logger: ILogger
  protected _network: Network
  protected _wallets?: { [address: string]: ECPair } = null

  protected _transaction: RawTransaction // 交易对象
  protected _unspents: IBTCUnspent[] // 转出地址总 unspents
  protected _inputs: IBTCInput[] // 所有的输入
  protected _outputs: IBTCOutput[] // 所有的输出
  protected _changes: IBTCOutput[] // 所有找零输出
  protected _value: number // 转出地址总余额
  protected _fee: number = 0 // 矿工费

  protected _froms: IBTCTransactionAddress[] // 转出地址
  protected _tos: IBTCTransactionAddress[] // 转入地址
  protected _memos: string[] = [] // 备注
  /**
   * 自动修正是因为用户想要全部转出时，很难精确的计算出 `总余额 - 矿工费` 的 `可转金额` ，这种模式下会自动将 `可转金额`
   * 限制在 `总余额 - 矿工费` 的范围之内。
   */
  protected _autoFix = false // 是否允许自动修正转出金额

  get value () {
    return this._value
  }

  get change () {
    return sumBy(this._changes, 'value')
  }

  get fee () {
    return this._fee
  }

  get unspents () {
    return this._unspents
  }

  constructor (
    froms: IBTCTransactionAddress[],
    tos: IBTCTransactionAddress[],
    changeAddress: string,
    memos: string[] = [],
    autoFix = false
  ) {
    this._validateAddressParams(froms)
    this._validateAddressParams(tos, 'to')
    this._validateAddressValues(tos)
    this._validateMemo(memos)

    this._froms = froms
    this._tos = tos
    this._memos = memos
    this._autoFix = autoFix
    this._changes = [
      {
        address: changeAddress,
        value: 0,
      },
    ]
  }

  async init (
    apiProvider: IApiProvider,
    logger: ILogger,
    network: Network,
    wallets: { [address: string]: ECPair } = null,
  ) {
    this._apiProvider = apiProvider
    this._logger = logger
    this._network = network
    this._wallets = wallets

    await this._loadUnspents(this._froms)
    this._calcInputOutput(this._unspents, this._tos, 0, this._autoFix)
    this._transaction = this._buildTransaction(this._inputs, this._outputs, this._changes, this._memos)
  }

  /**
   * 编辑交易内容
   *
   * 使用这个接口可以避免重复查询 unspents ，froms 不能修改，是因为 froms 一旦修改就要重新查询 unspents 。
   *
   * @param {IBTCTransactionAddress[]} [tos=[]]
   * @param {string} [changeAddress='']
   * @param {string[]} [memos=[]]
   * @param {number} [fee=-1]
   * @memberof Transaction
   */
  edit (fee: number = -1, tos: IBTCTransactionAddress[] = [], memos: string[] = [], changeAddress: string = '') {
    if (isNumber(fee) && fee > 0) {
      this._fee = fee
    }
    if (Array.isArray(tos) && tos.length > 0) {
      this._validateAddressParams(tos, 'to')
      this._validateAddressValues(tos)
      this._tos = tos
    }
    if (Array.isArray(tos) && memos.length > 0) {
      this._validateMemo(memos)
      this._memos = memos
    }
    if (changeAddress && isValidAddress(changeAddress)) {
      this._changes[0].address = changeAddress
    }

    this._calcInputOutput(this._unspents, this._tos, this._fee, this._autoFix)
    this._transaction = this._buildTransaction(this._inputs, this._outputs, this._changes, this._memos)
  }

  /**
   * 试算交易的矿工费
   *
   * @param {number} feeRate
   * @returns
   * @memberof Transaction
   */
  calcFee (feeRate: number) {
    this._logger.debug('BTC - Transaction.calcFee calculate transaction fee.')
    this._validateFeeRate(feeRate)

    // 计算矿工费
    const virtualSizeBefore = this._transaction.virtualSize()
    let virtualSize = virtualSizeBefore
    let fee = feeRate * virtualSize

    // 校验新的 feeRate 下交易的体积
    // 关于为什么需要校验：https://bitcoin.stackexchange.com/questions/1195/how-to-calculate-transaction-size-before-sending-legacy-non-segwit-p2pkh-p2sh
    const transaction = this._buildTransaction(this._inputs, this._outputs, this._changes, this._memos)
    const virtualSizeAfter = transaction.virtualSize()
    // 新的 feeRate 下交易的体积发生了变化，那么就以变化后的体积为准，通常这会导致多付一点手续费，但是比少付更能确保交易的及时性
    if (virtualSizeBefore !== virtualSizeAfter) {
      this._logger.debug('BTC - Transaction.calcFee recalculate transaction fee.')
      virtualSize = virtualSizeAfter
      fee = feeRate * virtualSize
    }

    this._logger.debug(`BTC - Transaction.calcFee totalFee[${fee}] = feeRate[${feeRate}] * virtualSize[${virtualSize}]`)
    return fee
  }

  /**
   * 生成 hex 编码交易数据
   *
   * @returns
   * @memberof Transaction
   */
  toHex () {
    return this._transaction.toHex()
  }

  /**
   * 生成 JSON 格式的交易数据
   *
   * @returns
   * @memberof Transaction
   */
  toJSON () {
    return parseRawTransaction(this._transaction.toHex(), this._inputs)
  }

  /**
   * 载入 unspents
   *
   * @protected
   * @param {IBTCTransactionAddress[]} froms
   * @memberof Transaction
   */
  protected async _loadUnspents (froms: IBTCTransactionAddress[]) {
    this._logger.debug('BTC - Transaction._loadUnspents')
    this._logger.trace('BTC - Transaction._loadUnspents from addresses: ', froms)

    let allUnspents: IBTCUnspent[] = []

    // 查询支付地址的 unspent ，总是查出所有地址的 unspent 一是为了统一统计，而是这样更利于找出最有的 input 组合
    if (this._apiProvider.supportBatch) {
      const unspents = await (<IFastApiProvider> this._apiProvider).getUnspentOfAddresses(froms.map(from => from.address))
      allUnspents = unspents
    }
    else {
      // TODO 通过 Promise.all 来增加同时请求数
      for (const from of froms) {
        const unspents = await (<ISlowApiProvider> this._apiProvider).getUnspentOfAddress(from.address)
        allUnspents = allUnspents.concat(unspents)
      }
    }

    this._unspents = allUnspents
  }

  /**
   * 计算交易输入输出
   *
   * WARNING 支付地址必须和 keypairs 中的地址一致，因为只有地址无法倒退回 publicKey ，那么假设 keypair 中用的是 p2pkh ，
   * 但是支付地址是 p2wpkh ，那么想要根据支付地址找到对应的 privateKey 就必须把每个 keypair 用来尝试生成 p2wpkh
   *
   * @param froms
   * @param tos
   * @param changeAddress
   */
  protected _calcInputOutput (
    unspents: IBTCUnspent[],
    tos: IBTCTransactionAddress[],
    fee: number,
    autoFix: boolean
  ) {
    this._logger.debug('BTC - Transaction._calcInputOutput')
    this._logger.trace('BTC - Transaction.calcInputOutput for tos: ', tos)

    const availableValue = sumBy(unspents, 'value')
    const requiredValue = sumBy(tos, 'value') + fee

    // 复制一份是为了避免重复使用时，不小心导致污染
    unspents = cloneDeep(unspents)
    tos = cloneDeep(tos)

    // 如果总金额不足，且不进行自动修正，那就报错
    if (!autoFix && requiredValue > availableValue) {
      this._logger.error('BTC - Transaction.calcInputOutput insufficient balance.')
      if (requiredValue - fee <= availableValue) {
        throw OneChainError.fromCode(109)
      }
      else {
        throw OneChainError.fromCode(102)
      }
    }

    // 有真实的 fee 时，将 fee 作为一个转出传入 coinselect ，这样能够避免遗漏支付 fee 所需的 inputs
    if (fee > 0) {
      tos.push({ address: 'fee', value: fee })
    }

    // WARNING coinselect 只接受 number 类型的 value
    // WARNING coinselect 的 fee 计算不准确，所以 feeRate 都用 0
    let selected = selectByBlackjack(unspents, tos, 0)

    // 如果没有找到可用的 unspent 组合，说明是余额不足，进行自动修正
    if (!selected.inputs || !selected.outputs) {
      this._logger.debug('BTC - Transaction.calcInputOutput auto fix.')

      // 发生自动修正时必定用上所有的 inputs ，所以这里删除占位 output
      const index = findIndex(tos, ['address', 'fee'])
      tos.splice(index, 1)

      // 当 tos 不指定 value 时，split 算法会自动转出全部 unspents
      // WARNING split 算法只支持均摊每个地址的收款金额，也就是说单地址没问题，多地址此算法可能需要重新定制
      const fixedTos = tos.map(to => ({ address: to.address }))
      selected = selectBySplit(unspents, fixedTos, 0)

      // 有真实的 fee 时，要从 output 中扣除 fee 的部分
      if (fee > 0) {
        selected.outputs = this._removeFeeFromOutputs(selected.outputs, fee)
      }

      this._changes[0].value = 0
    }
    // 找到可用组合，说明余额充足
    else {
      this._logger.debug('BTC - Transaction.calcInputOutput no auto fix.')

      // 找出找零 output ，这个 output 的特点是没有 address
      const index = findIndex(selected.outputs, (output: any) => !output.address)
      const output = selected.outputs[index]
      // 如果有一个找零 output ，将其放到 _changes 中管理
      if (output) {
        selected.outputs.splice(index, 1)
        this._changes[0].value = output.value
      }
      // 如果没有找零 output ，那就将找零设为 0
      else {
        this._changes[0].value = 0
      }

      // 有真实的 fee 时，因为其中有个 output 是为 fee 预留的，所以将其删除就算是留出 fee 了
      if (fee > 0) {
        const index = findIndex(selected.outputs, ['address', 'fee'])
        if (index !== -1) {
          selected.outputs.splice(index, 1)
        }
      }
    }

    this._logger.trace('BTC - Transaction.calcInputOutput selected inputs and outputs: ', selected.inputs, selected.outputs)
    this._value = sumBy(selected.outputs, 'value')
    this._inputs = selected.inputs
    this._outputs = selected.outputs
  }

  /**
   * 从最后一个 output 开始尝试扣除 fee 的部分
   *
   * @protected
   * @param {*} outputs
   * @param {*} fee
   * @returns
   * @memberof Transaction
   */
  protected _removeFeeFromOutputs (outputs, fee) {
    if (fee <= 0) {
      return outputs
    }

    // 这里的算法是从后往前，逐个从 output 的 value 中扣除矿工费，如果 value 不能覆盖全部矿工费，就删除当前 output
    // 也就是说矿工费优先由后面的 output 承担。
    for (let i = outputs.length - 1; i >= 0; i--) {
      const output = outputs[i]
      if (output.value > fee) {
        output.value -= fee
        fee = 0
        break
      }
      else {
        fee -= output.value
        output.value = 0
      }
    }
    // 发生自动修正意味着已经用掉了所有可用的 unspents ，而所有的 outputs 就是转出总额，所以当所有的 outputs 都用上
    // 之后 fee 还有剩余，那就说明遇到了转出金额很小矿工费很大的情况，这时转出金额甚至不足以支付矿工费。
    if (fee > 0) {
      throw OneChainError.fromCode(109)
    }
    // 如果 outputs 只剩一个，且 value 为 0 ，说明全部用来支付矿工费了，那么也报错
    if (outputs.length <= 1 && outputs[0].value === 0) {
      throw OneChainError.fromCode(116)
    }

    outputs = filter(outputs, output => output.value > 0)
    return outputs
  }

  /**
   * 构造 bitcoinjs 交易对象
   *
   * @protected
   * @param {IBTCInput[]} inputs
   * @param {IBTCOutput[]} outputs
   * @param {IBTCOutput[]} changes
   * @param {string[]} [memos=[]]
   * @returns
   * @memberof Transaction
   */
  protected _buildTransaction (
    inputs: IBTCInput[],
    outputs: IBTCOutput[],
    changes: IBTCOutput[],
    memos: string[] = [],
  ) {
    this._logger.debug('BTC - Transaction._buildTransaction')

    // 构造 transaction
    const builder = new TransactionBuilder(this._network)
    const signList = []

    // 添加 input
    inputs.forEach((input, index) => {
      builder.addInput(input.txId, input.vout)
      if (this._wallets) {
        const ecpair = this._wallets[input.address]
        signList.push({ index, ecpair })
      }
    })

    // 添加 output
    const changesNotEmpty = filter(changes, change => change.value > 0)
    outputs.concat(changesNotEmpty).forEach(output => {
      builder.addOutput(output.address, output.value)
    })

    // 添加 memo ，实际上就是 OP_RETURN
    memos.forEach(memo => {
      const buf = Buffer.from(memo, 'utf8')
      // @ts-ignore: embed 确实存在的
      const embed = payments.embed({ data: [buf] })
      builder.addOutput(embed.output, 0)
    })

    if (this._wallets) {
      // 签名必须放到添加 output 之后
      signList.forEach(v => {
        builder.sign(v.index, v.ecpair)
      })

      return builder.build()
    }
    else {
      return builder.buildIncomplete()
    }
  }

  /**
   * 验证 memo 参数
   *
   * 当 memo 不为空时检查其 byte 长度
   *
   * @protected
   * @param {string[]} [memos=[]]
   * @returns
   * @memberof Transaction
   */
  protected _validateMemo (memos: string[] = []) {
    if (!Array.isArray(memos)) {
      throw OneChainError.fromCode(113)
    }
    else if (memos.length > 0) {
      for (const memo of memos) {
        if (!memo) {
          throw OneChainError.fromCode(112)
        }

        const buf = Buffer.from(memo, 'utf8')
        if (buf.length > 80) {
          throw OneChainError.fromCode(108)
        }
      }
    }

    return true
  }

  /**
   * 验证转账地址相关参数
   *
   * @protected
   * @param {IBTCTransactionAddress[]} addresses
   * @param {string} [type='from']
   * @memberof Transaction
   */
  protected _validateAddressParams (addresses: IBTCTransactionAddress[], type = 'from') {
    if (!Array.isArray(addresses)) {
      throw OneChainError.fromCode(type === 'from' ? 114 : 115)
    }
    else if (!addresses || addresses.length <= 0) {
      throw OneChainError.fromCode(type === 'from' ? 103 : 104)
    }
  }

  /**
   * 验证转账金额
   *
   * @protected
   * @param {IBTCTransactionAddress[]} addresses
   * @memberof Transaction
   */
  protected _validateAddressValues (addresses: IBTCTransactionAddress[]) {
    addresses.forEach(address => {
      if (!isNumber(address.value) || address.value <= 0) {
        throw OneChainError.fromCode(24)
      }
    })
  }

  /**
   * 验证手续费费率
   *
   * @protected
   * @param {*} feeRate
   * @memberof Transaction
   */
  protected _validateFeeRate (feeRate) {
    if (feeRate <= 0) {
      throw OneChainError.fromCode(105)
    }
  }
}

export default Transaction
